import { useState } from "react"
import { checkPriorStanding } from "../../../../service/student-status"

type Prior = {
  grNumber: string
  name: string
  statusLabel: string
  previouslyLeft: boolean
  reAdmissionFeeApplies: boolean
  lastDeparture: { statusLabel: string; reason: string; date: string } | null
}

// "Has this child been here before?"
//
// The meeting asked for a flag at admission time showing that a student was
// previously Left Without Information. It matters for money: a child who left
// and came back pays the re-admission fee, and nobody can charge it if they do
// not know. This form never asks for a GR — one is generated later — so the
// check is offered as its own small step rather than hanging off a field.
const PriorStandingCheck = () => {
  const [gr, setGr] = useState("")
  const [checking, setChecking] = useState(false)
  const [result, setResult] = useState<Prior | null>(null)
  const [notFound, setNotFound] = useState(false)

  const check = async () => {
    if (!gr.trim()) return
    setChecking(true)
    setResult(null)
    setNotFound(false)
    try {
      const r = await checkPriorStanding(gr.trim())
      if (r.found && r.data) setResult(r.data as Prior)
      else setNotFound(true)
    } catch {
      setNotFound(true)
    } finally {
      setChecking(false)
    }
  }

  const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "—")

  return (
    <div className="card mb-3">
      <div className="card-body py-3">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <div>
            <label className="form-label mb-1">Has this student been here before?</label>
            <div className="text-muted small">
              Enter their old GR number to check — optional, but a returning student
              pays the re-admission fee.
            </div>
          </div>
          <div className="ms-auto d-flex gap-2">
            <input
              className="form-control"
              style={{ width: 200 }}
              placeholder="Old GR number"
              value={gr}
              onChange={(e) => setGr(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  check()
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={check}
              disabled={checking || !gr.trim()}
            >
              {checking ? "Checking…" : "Check"}
            </button>
          </div>
        </div>

        {notFound && (
          <div className="alert alert-light border text-dark py-2 small mt-3 mb-0">
            No student found with GR {gr.trim()} — treat this as a fresh admission.
          </div>
        )}

        {result && !result.previouslyLeft && (
          <div className="alert alert-light border text-dark py-2 small mt-3 mb-0">
            <strong>{result.name}</strong> (GR {result.grNumber}) is currently{" "}
            <strong>{result.statusLabel}</strong> — still on the rolls, so this is not a
            re-admission.
          </div>
        )}

        {result?.previouslyLeft && (
          <div className="alert alert-light border-start border-danger border-3 text-dark py-2 small mt-3 mb-0">
            <div>
              <strong>{result.name}</strong> (GR {result.grNumber}) has been here before
              and left as <strong>{result.lastDeparture?.statusLabel || result.statusLabel}</strong>
              {result.lastDeparture?.date && ` on ${fmt(result.lastDeparture.date)}`}.
            </div>
            {result.lastDeparture?.reason && (
              <div className="mt-1">Reason recorded: {result.lastDeparture.reason}</div>
            )}
            {result.reAdmissionFeeApplies && (
              <div className="mt-1">
                <strong>The Re-Admission Fee applies to this student.</strong> Charge it
                through Fees before putting them back on the rolls.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default PriorStandingCheck
